<template>
    <div class="row">
        <div class="col-xs-12">
            <div class="ibox">
                <div class="ibox-title">
                    <h1>Edit message group</h1>
                </div>
                <div class="ibox-content">
                    <div v-if="!existingLoaded">
                        <spinner></spinner>
                    </div>
                    <div v-if="error">
                        <p class="text-danger">
                            {{ errorMessage }}
                        </p>
                    </div>
                    <div v-if="existingLoaded" class="grid grid-cols-2 sm-grid-cols-1 md-grid-cols-1 gap-xl">
                        <div>
                          <div class="m-t-sm form-group">
                            <label for="group_name">Group Name</label>
                            <small>e.g. rides, varsity travel team, tournament help, etc</small>
                            <input v-model="group.name" type="text" id="group_name" class="form-control" maxlength="50"/>
                          </div>
                          <div class="m-t-sm form-group">
                            <label for="group_purpose">Group Purpose</label>
                            <small>e.g. "Used by parents and coaches to organize rides for tournaments."</small>
                            <input v-model="group.purpose" type="text" id="group_purpose" class="form-control" maxlength="75"/>
                          </div>
                          <div class="m-t-sm form-group">
                            <div class="m-t-md">
                              <label>
                                Who should be able to send messages within the group?
                              </label>
                              <div>
                                <input type="radio" value="all" id="all_related_events" v-model="group.default_allow_publish"> Everyone
                              </div>
                              <div>
                                <input type="radio" value="admins" id="only_this_event" v-model="group.default_allow_publish"> Only coaches and admins
                              </div>
                            </div>
                          </div>
                          <div v-if="!showReSyncForm && isNotDirectGroup" class="m-t-lg">
                            <div class="form-group">
                              <div class="flex justify-space-between align-items-baseline">
                                <div>
                                  <label>Sync message group with rosters?</label>
                                  <VDropdown theme="info-tooltip">
                                    <!-- This will be the popover target (for the events and position) -->
                                    <i class="fa fa-question"></i>
                                    <!-- This will be the content of the popover -->
                                    <template v-slot:popper>
                                      <div>
                                        <p class="header">
                                          Why would I want to sync a message group with a roster?
                                        </p>
                                        <p>
                                          If you sync a message group with a roster, wrestlers (and their guardians, if specified) on that roster will be added to the message group automatically, without you having to do anything. Similarly, if a wrestler is removed from a roster, they will be removed from the message group.
                                        </p>
                                        <p>
                                          Coaches that effectively use WrestlingIQ will often create a message group for each roster, and then sync the message group with the roster. This allows them to send messages to all the parents of the wrestlers on the roster, without having to manually add each parent to the message group.
                                        </p>
                                      </div>
                                    </template>
                                  </VDropdown>
                                </div>
                                <div>
                                  <a @click.prevent="showReSyncForm = true" class="btn btn-default btn-compact flex gap-x-xs justify-center align-items-center">
                                    <i class="fa fa-refresh"></i>
                                    Resync
                                  </a>
                                </div>
                              </div>
                              <div class="m-t-sm">
                                <search
                                    class="search-form"
                                    response-key="rosters"
                                    placeholder="Type name of roster..."
                                    :mutate-history="false"
                                    :allow-add="false"
                                    :small-input="true"
                                    :display-all-on-empty="false"
                                    readable-model-name="roster"
                                    base-url="/api/v1/rosters"
                                >
                                  <template v-slot:list="slotProps">
                                    <div
                                        class="border-top m-t-sm m-b-xs p-t-sm p-b-xs cursor-pointer flex justify-space-between align-items-center p-r-sm"
                                        @click="rosterSyncClicked(slotProps.result)"
                                    >
                                      <div>
                                        {{ slotProps.result.name }}
                                      </div>
                                      <div
                                          class="border rounded-sm border-blue-200 border-solid font-small"
                                          style="padding: 2px 6px"
                                      >
                                        Sync
                                      </div>
                                    </div>
                                  </template>
                                </search>
                                <div class="flex flex-wrap gap-x-xxs gap-y-xxs m-t-sm">
                                  <div
                                      v-for="sync in notDestroyedSyncers"
                                      class="flex gap-x-sm btn btn-success btn-outline rounded-xs font-small"
                                      style="padding: 4px 8px"
                                  >
                                    <div class="flex flex-col align-items-start gap-xxs">
                                      <span>{{ sync.roster.name }}</span>
                                      <label class="m-b-none flex align-items-center gap-x-xxs">
                                        <input 
                                          type="checkbox"
                                          class="m-t-none"
                                          style="margin-top: 0px;"
                                          :checked="sync.include_guardians"
                                          :disabled="!!sync.id"
                                          @change="toggleGuardians(sync)"
                                        />
                                        <small class="font-normal">Include guardians</small>
                                      </label>
                                    </div>
                                    <i class="fa fa-times cursor-pointer" @click="rosterSyncClicked(sync.roster)"></i>
                                  </div>
                                  <div
                                      v-for="sync in destroyedSyncers"
                                      class="flex align-items-center gap-x-xs btn btn-danger btn-outline rounded-lg font-small"
                                      style="padding: 2px 6px"
                                  >
                                    <span>{{ sync.roster.name }} (will be removed)</span>
                                    <i class="fa fa-times cursor-pointer" @click="rosterSyncClicked(sync.roster)"></i>
                                  </div>
                                </div>
                                <div class="m-t-xs" v-if="notDestroyedSyncers.length > 0 || destroyedSyncers.length > 0">
                                  <span v-if="notDestroyedSyncers.length > 0">
                                    <strong>Note:</strong> syncing of members based on
                                    rosters does not happen until after you press
                                    save.<br />
                                  </span>
                                  <span v-if="destroyedSyncers.length > 0">
                                    <strong>Note:</strong> When removing a roster, the members of that roster will be removed from this message group unless they are part of another synced roster.<br />
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div v-if="showReSyncForm && isNotDirectGroup" class="m-t-lg">
                            <h3>Are you sure you want to resync?</h3>
                            <p class="text-heavy">
                              Resyncing will remove all members from this message group, then re-add the members who are synced via the specific rosters. Coaches are unaffected by this process.
                            </p>
                            <p class="text-heavy">
                              If you have manually added members to this message group, they will be removed.
                            </p>
                            <div class="flex gap-x-xs">
                              <a class="btn btn-default" @click.prevent="showReSyncForm = false">
                                Cancel
                              </a>
                              <ladda-button @lbClicked="triggerSync" el-class="btn-primary" :loading="reSyncingRoster">
                                Yes, re-sync message group
                              </ladda-button>
                            </div>
                          </div>
                          <div class="m-t-lg" v-if="!showReSyncForm">
                            <ladda-button @lbClicked="saveGroup" el-class="btn-primary" :loading="loading">
                              Save
                            </ladda-button>
                            <div>
                              <small v-show="error"><span class="text-danger">{{ errorMessage }}</span></small>
                            </div>
                          </div>
                        </div>
                        <div>
                          <div class="m-t-sm relative border border-solid border-green-600 rounded-xxs">
                            <div class="flex align-items-center justify-center gap-sm bg-green-600 text-green-50 uppercase tracking-wide p-x-m p-y-xs -m-sm rounded-t-xxs">
                              <span class="text-heavy">WIQ RECOMMENDATION</span>
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6 text-green-50"><path stroke-linecap="round" stroke-linejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18"></path>
                              </svg>
                            </div>
                            <div class="p-x-lg p-y-md grid grid-cols-2 gap-y-sm gap-x-lg sm-grid-cols-1 md-grid-cols-1 m-t-sm">
                              <div>
                                <h1 class="m-t-none font-heavy">
                                  Looking to add/remove people to the group or modify sending permissions for a single person?
                                </h1>
                                <p>
                                  See the <a :href="`/message_groups/${group.id}/edit_memberships`">Edit Group Members</a> page to add or remove people from the group, or change the sending permissions of a single person in the group.
                                </p>
                              </div>
                              <div class="flex flex-col gap-xl justify-center w-full p-x-md">
                                <div class="flex flex-col gap-sm justify-center align-items-center">
                                  <a :href="`/message_groups/${group.id}/edit_memberships`" class="btn btn-primary btn-block w-full justify-center flex align-items-center gap-x-xs"><i class="fa fa-pencil"></i> Edit Group Members</a>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import LaddaButton from '../../shared/ladda_button.vue';
import Spinner from './spinner.vue';
import Search from '../../shared/search.vue';

export default {
  name: 'edit-message-group-form',
  components: { LaddaButton, Spinner, Search },
  props: {
    existingGroup: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      existingLoaded: false,
      loading: false,
      error: false,
      errorMessage: '',
      group: {
        name: '',
        purpose: '',
        default_allow_publish: 'all',
      },
      messageGroupRosterSyncers: {},
      showReSyncForm: false,
      reSyncingRoster: false,
    };
  },
  created() {
    this.group = this.existingGroup;
    this.messageGroupRosterSyncers = _.keyBy(
      _.map(this.existingGroup.message_group_roster_syncers, (rs) => {
        rs._destroy = false;
        return rs;
      }),
      'roster.id'
    );

    // Use Vue.set or this.$set to ensure reactivity for the messageGroupRosterSyncers object
    this.messageGroupRosterSyncers = {};
    const syncers = _.map(this.existingGroup.message_group_roster_syncers, (rs) => {
      return { ...rs, _destroy: false };
    });
    
    // Properly set each syncer with $set to ensure reactivity
    syncers.forEach(syncer => {
      this.$set(this.messageGroupRosterSyncers, syncer.roster.id, syncer);
    });

    this.loading = false;
    this.existingLoaded = true;
  },
  computed: {
    notDestroyedSyncers() {
      return _.flatMap(this.messageGroupRosterSyncers).filter((rs) => !rs._destroy);
    },
    destroyedSyncers() {
      return _.flatMap(this.messageGroupRosterSyncers).filter((rs) => rs._destroy);
    },
    isNotDirectGroup() {
      return this.group.group_type !== 'direct';
    },
  },
  methods: {
    toggleGuardians(sync) {
      this.$set(sync, 'include_guardians', !sync.include_guardians);
    },
    rosterSyncClicked(roster) {
      this.$notificationManager.$emit("profile-clicked");
      if (this.messageGroupRosterSyncers[roster.id]) {
        // If we already have a syncer check to see if we have an id
        let sync = this.messageGroupRosterSyncers[roster.id];
        if (sync.id) {
          // We have an existing message group roster syncer on the backend, so we toggle the destroy flag
          this.$set(this.messageGroupRosterSyncers[roster.id], '_destroy', !sync._destroy);
        } else {
          // We do not have an existing message group roster syncer on the backend, so we simply drop it from the payload
          this.$delete(this.messageGroupRosterSyncers, roster.id);
        }
        return;
      }

      // In this world we do not have a roster id so we set it
      let syncer = {
        _destroy: false,
        roster: {
          id: roster.id,
          name: roster.name,
        },
        include_guardians: true,
      };
      this.$set(this.messageGroupRosterSyncers, roster.id, syncer);
    },
    getSyncerParams() {
      return _.flatMap(this.messageGroupRosterSyncers, (rs) => {
        if (rs.id) {
          return {
            id: rs.id,
            roster_id: rs.roster.id,
            include_guardians: rs.include_guardians,
            _destroy: rs._destroy,
          };
        }

        return {
          roster_id: rs.roster.id,
          include_guardians: rs.include_guardians,
          _destroy: rs._destroy,
        };
      });
    },
    triggerSync() {
      if (this.reSyncingRoster) {
        return;
      }
      this.reSyncingRoster = true;
      const url = this.$apiService.messageGroupSyncUrl(this.group.id);
      axios.post(url)
        .then((response) => {
          this.$notificationManager.$emit("show-toast", "Processing message group re-sync...", true);
          
          // Extract the batch ID from the response
          const batchId = response.data.batch_id;
          
          // Set up polling for batch status
          if (batchId) {
            this.pollBatchStatus(batchId);
          } else {
            // Fallback if no batch ID is received
            window.location.assign(`/message_groups/${this.group.id}`);
          }
        })
        .catch((error) => {
          this.error = true;
          this.errorMessage = `Error re-syncing message group ${error.toString()}`;
          this.reSyncingRoster = false;
        });
    },
    pollBatchStatus(batchId) {
      const vm = this;
      const pollInterval = 500; // Poll every 500ms
      const batchStatusUrl = `/batch_status/${batchId}.json`;
      
      const checkStatus = () => {
        axios.get(batchStatusUrl)
          .then(response => {
            const status = response.data;
            // Check if the batch is complete
            if (status.is_complete) {
              vm.$notificationManager.$emit("show-toast", "Message group re-sync complete", true);
              window.location.assign(`/message_groups/${vm.group.id}`);
            } else {
              // Continue polling
              setTimeout(checkStatus, pollInterval);
            }
          })
          .catch(error => {
            vm.error = true;
            vm.errorMessage = `Error checking batch status: ${error.toString()}`;
            // If polling fails, redirect after a short delay
            setTimeout(() => {
              window.location.assign(`/message_groups/${vm.group.id}`);
            }, 2000);
          });
      };
      
      // Start polling
      checkStatus();
    },
    saveGroup: _.throttle(function () {
      const vm = this;
      if (vm.loading) {
        return;
      }

      vm.error = false;
      if (vm.group.name.length === 0) {
        vm.errorMessage = 'Please ensure the group has a name';
        vm.error = true;
        return;
      }

      vm.loading = true;
      const params = {
        message_group: {
          name: vm.group.name,
          purpose: vm.group.purpose,
          default_allow_publish: vm.group.default_allow_publish,
          message_group_roster_syncers_attributes: vm.getSyncerParams(),
        },
      };

      const url = vm.$apiService.messageGroupUrl(vm.group.id);
      axios.put(url, params)
        .then((response) => {
          vm.loading = false;
          vm.$notificationManager.$emit('show-toast', `${vm.group.name} saved`, true);
          window.location.assign(`/message_groups/${vm.group.id}`);
        }).catch((error) => {
          vm.loading = false;
          vm.errorMessage = 'Error saving message group, please contact support@wrestlingiq.com';
          vm.error = true;
        });
    }, 500),
  },
};
</script>
